import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import apiService from '../../services/api';

const TwoFactorSetup = ({ open, onClose, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const steps = [
    'Setup 2FA',
    'Scan QR Code', 
    'Verify Code',
    'Save Backup Codes'
  ];

  useEffect(() => {
    if (open && activeStep === 0) {
      initiate2FASetup();
    }
  }, [open]);

  const initiate2FASetup = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService.request('/api/auth/2fa/setup', {
        method: 'POST'
      });
      setSetupData(response);
      setActiveStep(1);
    } catch (err) {
      setError(err.message || 'Failed to setup 2FA');
    }
    setLoading(false);
  };

  const verifySetup = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await apiService.request('/api/auth/2fa/verify', {
        method: 'POST',
        data: {
          totp_code: verificationCode
        }
      });
      setActiveStep(3);
    } catch (err) {
      setError(err.message || 'Invalid verification code');
    }
    setLoading(false);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 1:
        return (
          <Box textAlign="center">
            <Typography variant="h6" gutterBottom>
              Scan QR Code
            </Typography>
            
            {setupData?.qr_code_url && (
              <Box display="flex" justifyContent="center" my={2}>
                <img 
                  src={setupData.qr_code_url} 
                  alt="2FA QR Code" 
                  style={{ width: 200, height: 200 }}
                />
              </Box>
            )}

            <Typography variant="body2" color="textSecondary">
              Secret: {setupData?.secret}
            </Typography>

            <Box mt={3}>
              <Button 
                variant="contained" 
                onClick={() => setActiveStep(2)}
              >
                I've Added the Account
              </Button>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box textAlign="center">
            <Typography variant="h6" gutterBottom>
              Enter Verification Code
            </Typography>
            
            <TextField
              label="6-digit code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              inputProps={{ maxLength: 6 }}
              autoFocus
            />

            <Box mt={3}>
              <Button 
                variant="contained" 
                onClick={verifySetup}
                disabled={loading}
              >
                Verify
              </Button>
            </Box>
          </Box>
        );

      case 3:
        return (
          <Box textAlign="center">
            <Typography variant="h6" gutterBottom color="success.main">
              2FA Enabled Successfully!
            </Typography>
            
            <Typography variant="body2" paragraph>
              Backup codes:
            </Typography>

            <Paper sx={{ p: 2, my: 2 }}>
              {setupData?.backup_codes?.map((code, index) => (
                <Typography key={index} variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {code}
                </Typography>
              ))}
            </Paper>

            <Button 
              variant="contained" 
              onClick={() => {
                onSuccess();
                onClose();
              }}
            >
              Complete
            </Button>
          </Box>
        );

      default:
        return (
          <Typography>Setting up 2FA...</Typography>
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Two-Factor Authentication Setup</DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TwoFactorSetup;
